import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, Sun, ChevronRight } from 'lucide-react';

const EDGE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-questionnaire`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

interface FormData {
  first_name: string;
  last_name: string;
  address: string;
  ean_code: string;
  domicile_address: string;
  national_register: string;
  supply_address: string;
  supply_address_different: boolean;
}

type Status = 'loading' | 'not_found' | 'already_filled' | 'form' | 'submitting' | 'success';

export default function QuestionnairePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<Status>('loading');
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [form, setForm] = useState<FormData>({
    first_name: '',
    last_name: '',
    address: '',
    ean_code: '',
    domicile_address: '',
    national_register: '',
    supply_address: '',
    supply_address_different: false,
  });

  useEffect(() => {
    if (!token) { setStatus('not_found'); return; }
    verifyToken(token);
  }, [token]);

  const verifyToken = async (t: string) => {
    try {
      const res = await fetch(`${EDGE_URL}?token=${encodeURIComponent(t)}`, {
        headers: { 'Authorization': `Bearer ${ANON_KEY}`, 'apikey': ANON_KEY },
      });
      if (!res.ok) { setStatus('not_found'); return; }
      const data = await res.json();
      if (data.status === 'filled') { setStatus('already_filled'); return; }
      setStatus('form');
    } catch {
      setStatus('not_found');
    }
  };

  const validate = (): boolean => {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (!form.first_name.trim()) e.first_name = 'Champ requis';
    if (!form.last_name.trim()) e.last_name = 'Champ requis';
    if (!form.address.trim()) e.address = 'Champ requis';
    if (!form.ean_code.trim()) e.ean_code = 'Champ requis';
    if (!form.domicile_address.trim()) e.domicile_address = 'Champ requis';
    if (!form.national_register.trim()) e.national_register = 'Champ requis';
    if (form.supply_address_different && !form.supply_address.trim()) e.supply_address = 'Champ requis si différent du domicile';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !token) return;
    setStatus('submitting');

    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${ANON_KEY}`,
          'apikey': ANON_KEY,
        },
        body: JSON.stringify({
          token,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          address: form.address.trim(),
          ean_code: form.ean_code.trim(),
          domicile_address: form.domicile_address.trim(),
          national_register: form.national_register.trim(),
          supply_address: form.supply_address_different ? form.supply_address.trim() : null,
        }),
      });

      const result = await res.json();

      if (res.status === 409 || result.error === 'already_filled') {
        setStatus('already_filled');
        return;
      }

      if (!res.ok) {
        setStatus('form');
        return;
      }

      setStatus('success');
    } catch {
      setStatus('form');
    }
  };

  const Field = ({
    label, name, value, onChange, error, placeholder, required = true,
  }: {
    label: string; name: keyof FormData; value: string; onChange: (v: string) => void;
    error?: string; placeholder?: string; required?: boolean;
  }) => (
    <div>
      <label className="block text-sm font-medium text-neutral-800 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-4 py-3 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 ${
          error
            ? 'border-red-300 focus:ring-red-200 bg-red-50'
            : 'border-neutral-200 focus:ring-amber-200 focus:border-amber-400 bg-white'
        }`}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-neutral-50 flex flex-col">
      <header className="py-6 px-6 border-b border-neutral-100 bg-white/80 backdrop-blur-sm">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <img src="/images/logo-v2.png" alt="Sun Is Up" className="w-10 h-10 object-contain" />
          <div>
            <p className="font-bold text-neutral-900 text-sm">Sun Is Up</p>
            <p className="text-xs text-neutral-500">Communauté d'énergie renouvelable</p>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl">

          {status === 'loading' && (
            <div className="text-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-4" />
              <p className="text-neutral-500 text-sm">Vérification du lien...</p>
            </div>
          )}

          {status === 'not_found' && (
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-10 text-center">
              <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-neutral-900 mb-2">Lien invalide</h2>
              <p className="text-neutral-500 text-sm">Ce lien est invalide ou a expiré. Contactez-nous à <a href="mailto:info@sunisup.be" className="text-amber-600 underline">info@sunisup.be</a>.</p>
            </div>
          )}

          {status === 'already_filled' && (
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-10 text-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className="text-xl font-bold text-neutral-900 mb-2">Questionnaire déjà complété</h2>
              <p className="text-neutral-500 text-sm">Vous avez déjà rempli ce questionnaire. Merci pour votre participation !</p>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-10 text-center">
              <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-xl font-bold text-neutral-900 mb-2">Merci !</h2>
              <p className="text-neutral-600 text-sm leading-relaxed">
                Vos informations ont bien été transmises à notre équipe.<br />
                Nous vous contacterons prochainement pour finaliser votre adhésion.
              </p>
              <div className="mt-6 pt-6 border-t border-neutral-100">
                <p className="text-xs text-neutral-400">Sun Is Up — <a href="mailto:info@sunisup.be" className="hover:underline">info@sunisup.be</a></p>
              </div>
            </div>
          )}

          {(status === 'form' || status === 'submitting') && (
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 overflow-hidden">
              <div className="px-8 py-6 border-b border-neutral-100" style={{ background: 'linear-gradient(135deg, #FFF5E1 0%, #fff 100%)' }}>
                <div className="flex items-center gap-2 mb-1">
                  <Sun className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Questionnaire d'adhésion</span>
                </div>
                <h1 className="text-2xl font-bold text-neutral-900">Vos informations</h1>
                <p className="text-sm text-neutral-500 mt-1">Remplissez ce formulaire pour rejoindre la communauté Sun Is Up. Tous les champs marqués d'un <span className="text-red-500">*</span> sont obligatoires.</p>
              </div>

              <form onSubmit={handleSubmit} className="p-8 space-y-6">

                <div>
                  <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                    <span className="w-5 h-5 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    Identité
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Prénom" name="first_name" value={form.first_name}
                      onChange={v => setForm(f => ({ ...f, first_name: v }))} error={errors.first_name}
                      placeholder="Jean" />
                    <Field label="Nom" name="last_name" value={form.last_name}
                      onChange={v => setForm(f => ({ ...f, last_name: v }))} error={errors.last_name}
                      placeholder="Dupont" />
                  </div>
                  <div className="mt-4">
                    <Field label="Numéro de registre national" name="national_register" value={form.national_register}
                      onChange={v => setForm(f => ({ ...f, national_register: v }))} error={errors.national_register}
                      placeholder="XX.XX.XX-XXX.XX" />
                  </div>
                </div>

                <div className="border-t border-neutral-100 pt-6">
                  <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                    <span className="w-5 h-5 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    Adresse & point de fourniture
                  </h3>
                  <div className="space-y-4">
                    <Field label="Adresse du domicile" name="domicile_address" value={form.domicile_address}
                      onChange={v => setForm(f => ({ ...f, domicile_address: v }))} error={errors.domicile_address}
                      placeholder="Rue de la Loi 1, 1000 Bruxelles" />

                    <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-neutral-100 hover:bg-neutral-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={form.supply_address_different}
                        onChange={e => setForm(f => ({ ...f, supply_address_different: e.target.checked, supply_address: '' }))}
                        className="mt-0.5 w-4 h-4 accent-amber-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-neutral-700">Adresse du point de fourniture différente</p>
                        <p className="text-xs text-neutral-400">Cochez si l'adresse où l'énergie est consommée est différente de votre domicile</p>
                      </div>
                    </label>

                    {form.supply_address_different && (
                      <Field label="Adresse du point de fourniture" name="supply_address" value={form.supply_address}
                        onChange={v => setForm(f => ({ ...f, supply_address: v }))} error={errors.supply_address}
                        placeholder="Rue de la Loi 2, 1000 Bruxelles" />
                    )}

                    <Field label="Adresse de facturation" name="address" value={form.address}
                      onChange={v => setForm(f => ({ ...f, address: v }))} error={errors.address}
                      placeholder="Rue de la Loi 1, 1000 Bruxelles" />
                  </div>
                </div>

                <div className="border-t border-neutral-100 pt-6">
                  <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                    <span className="w-5 h-5 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    Point de raccordement
                  </h3>
                  <Field label="Code EAN" name="ean_code" value={form.ean_code}
                    onChange={v => setForm(f => ({ ...f, ean_code: v }))} error={errors.ean_code}
                    placeholder="541400XXXXXXXXXX" />
                  <p className="text-xs text-neutral-400 mt-2">Le code EAN figure sur votre facture d'électricité ou contrat fournisseur. Il commence par 541.</p>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full py-4 rounded-xl font-semibold text-neutral-900 flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#F8BD3D' }}
                  >
                    {status === 'submitting' ? (
                      <><Loader2 className="w-4 h-4 animate-spin" />Envoi en cours...</>
                    ) : (
                      <>Envoyer mes informations<ChevronRight className="w-4 h-4" /></>
                    )}
                  </button>
                  <p className="text-xs text-neutral-400 text-center mt-3">Vos données sont traitées de manière confidentielle et ne sont utilisées que dans le cadre de votre adhésion à Sun Is Up.</p>
                </div>

              </form>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
